import React, { createContext, useContext, useState, useEffect } from 'react'

// Segment Context
const SegmentContext = createContext()

// Segment data configuration
export const SEGMENTS = {
  tech: {
    id: 'tech',
    label: {
      en: 'Systems',
      de: 'Systeme',
      es: 'Sistemas'
    },
    transitionText: {
      en: 'I develop web systems that connect thinking and design.',
      de: 'Ich entwickle Websysteme, die Denken und Design verbinden.',
      es: 'Desarrollo sistemas web que conectan pensamiento y diseño.'
    },
    claim: {
      en: 'Digital architectures for complex systems.',
      de: 'Digitale Architekturen für komplexe Systeme.',
      es: 'Arquitecturas digitales para sistemas complejos.'
    },
    cases: ['Eekual', 'WDR UX Framework', 'AI Image Engine'],
    route: '/'
  },
  art: {
    id: 'art',
    label: {
      en: 'Design',
      de: 'Gestaltung',
      es: 'Diseño'
    },
    transitionText: {
      en: 'I compose systems from sound, light and code.',
      de: 'Ich komponiere Systeme aus Klang, Licht und Code.',
      es: 'Compongo sistemas de sonido, luz y código.'
    },
    claim: {
      en: 'Audiovisual compositions between sound, light and code.',
      de: 'Audiovisuelle Kompositionen zwischen Klang, Licht und Code.',
      es: 'Composiciones audiovisuales entre sonido, luz y código.'
    },
    cases: ['Video/Snare', 'Ruidoperla', 'Telefante'],
    route: '/'
  }
}

const STORAGE_KEY = 'studio-negron-segment'
const SESSION_KEY = 'studio-negron-seen-survey'

// Provider Component
export const SegmentProvider = ({ children }) => {
  const [segment, setSegmentState] = useState(null)
  const [hasCompletedSurvey, setHasCompletedSurvey] = useState(false)
  const [isClient, setIsClient] = useState(false)

  // Initialize from URL parameters, then localStorage on mount
  useEffect(() => {
    setIsClient(true)
    if (typeof window !== 'undefined') {
      // Check URL parameters first
      const urlParams = new URLSearchParams(window.location.search)
      const urlSegment = urlParams.get('segment')
      
      if (urlSegment && SEGMENTS[urlSegment]) {
        setSegmentState(urlSegment)
        setHasCompletedSurvey(true)
        localStorage.setItem(STORAGE_KEY, urlSegment)
      } else {
        // Fallback to localStorage if no valid URL parameter
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored && SEGMENTS[stored]) {
          setSegmentState(stored)
          setHasCompletedSurvey(true)
        }
      }
    }
  }, [])

  const setSegment = (segmentId) => {
    if (SEGMENTS[segmentId]) {
      setSegmentState(segmentId)
      setHasCompletedSurvey(true)
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, segmentId)
        sessionStorage.setItem(SESSION_KEY, 'true')
        
        // Update URL parameter without page reload
        const url = new URL(window.location)
        url.searchParams.set('segment', segmentId)
        window.history.replaceState({}, '', url.toString())
      }
    }
  }

  const hasSeenSurveyThisSession = () => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem(SESSION_KEY) === 'true'
    }
    return false
  }

  const markSurveyAsSeen = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem(SESSION_KEY, 'true')
    }
  }

  const clearSegment = () => {
    setSegmentState(null)
    setHasCompletedSurvey(false)
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY)
      
      // Remove URL parameter
      const url = new URL(window.location)
      url.searchParams.delete('segment')
      window.history.replaceState({}, '', url.toString())
    }
  }

  const getSegmentData = (segmentId, language = 'en') => {
    const segment = SEGMENTS[segmentId] || SEGMENTS.tech

    // Return segment with localized strings
    return {
      ...segment,
      label: segment.label[language] || segment.label.en,
      transitionText: segment.transitionText[language] || segment.transitionText.en,
      claim: segment.claim[language] || segment.claim.en
    }
  }

  const value = {
    segment,
    setSegment,
    clearSegment,
    getSegmentData,
    hasCompletedSurvey,
    hasSeenSurveyThisSession,
    markSurveyAsSeen,
    isClient,
    segments: SEGMENTS
  }

  return (
    <SegmentContext.Provider value={value}>
      {children}
    </SegmentContext.Provider>
  )
}

// Custom Hook
export const useSegment = () => {
  const context = useContext(SegmentContext)
  if (!context) {
    throw new Error('useSegment must be used within a SegmentProvider')
  }
  return context
}

export default SegmentContext
